import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemberAuth } from "../../context/MemberAuthContext";
import { memberApi } from "../../lib/api";
import MemberLayout from "../../components/MemberLayout";
import { Avatar } from "../../components/MemberLayout";
import { toast } from "../../hooks/use-toast";
import { Upload, Save, ChevronDown, X, Check } from "lucide-react";

const SKILL_OPTIONS = ['Product','Engineering','Design','Strategy','Finance','Fundraising','Marketing','Operations','Legal','Data','Research','Sales','Growth','People','Community','Education','Policy'];
const INDUSTRY_OPTIONS = ['Technology','Finance','Education','Healthcare','Media','Consulting','Non-profit','Government','Manufacturing','Retail','Energy','Agriculture','Legal','Marketing','Real Estate'];

const SkillPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const all = Array.from(new Set([...SKILL_OPTIONS, ...value]));
  const toggle = (s) => onChange(value.includes(s) ? value.filter(x => x !== s) : [...value, s]);
  const addCustom = () => { const t = custom.trim(); if (t && !value.includes(t)) onChange([...value, t]); setCustom(""); };
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-white border text-[14px] text-left" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
        <span className="text-[#0d2a20]/70">{value.length ? `${value.length} selected` : 'Select skills'}</span>
        <ChevronDown size={16} className="text-[#0d2a20]/50" />
      </button>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.map(s => (
            <span key={s} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[12px]" style={{ background: '#e0684d', color: 'white' }}>
              {s}<button type="button" onClick={() => toggle(s)}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <div className="mt-2 rounded-md p-3 border" style={{ background: 'white', borderColor: 'rgba(13,42,32,0.15)' }}>
          <div className="flex flex-wrap gap-1 max-h-52 overflow-y-auto">
            {all.map(s => {
              const sel = value.includes(s);
              return (
                <button key={s} type="button" onClick={() => toggle(s)} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[12px] ${sel ? 'bg-[#e0684d] text-white' : 'bg-[#ece5d3] text-[#0d2a20]'}`}>
                  {sel && <Check size={11} />}{s}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'rgba(13,42,32,0.08)' }}>
            <input placeholder="Add a custom skill" value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} className="flex-1 px-2 py-1 rounded bg-[#f4efe4] border text-[12px]" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
            <button type="button" onClick={addCustom} className="px-3 py-1 rounded btn-green text-[12px]">Add</button>
          </div>
        </div>
      )}
    </div>
  );
};

const OptOutModal = ({ onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(13,42,32,0.55)' }}>
    <div className="w-full max-w-[520px] rounded-lg p-8" style={{ background: '#fbf8ef' }}>
      <h3 className="hero-title text-[26px] leading-tight mb-3">Opt out of the <span className="font-serif-i text-[#e0684d]">directory</span>?</h3>
      <p className="text-[14px] text-[#0d2a20]/80 leading-relaxed mb-6">By opting out of the Vemoye Network Member Directory, your profile will no longer be discoverable by other members. You will also lose access to the directory and will not be able to search for or view other members.</p>
      <div className="flex gap-2 justify-end flex-wrap">
        <button onClick={onCancel} className="px-4 py-2 rounded-md btn-green text-[13px]">Stay in Directory</button>
        <button onClick={onConfirm} className="px-4 py-2 rounded-md btn-coral text-[13px]">Opt Out</button>
      </div>
    </div>
  </div>
);

const Profile = () => {
  const { member, loading, updateProfile } = useMemberAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isFirstTime = loc.state?.firstTime;
  const fileRef = useRef(null);
  const [form, setForm] = useState({ display_name: '', headline: '', company: '', industry: '', skills: [], bio: '', location: '', linkedin: '', avatar_url: '', searchable: true, conversations_topics: [] });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmOptOut, setConfirmOptOut] = useState(false);

  useEffect(() => {
    if (!loading && !member) { nav('/login?next=/member/profile', { replace: true }); return; }
    if (member) setForm({
      display_name: member.display_name || '', headline: member.headline || '', company: member.company || '',
      industry: member.industry || '', skills: member.skills || [], bio: member.bio || '',
      location: member.location || '', linkedin: member.linkedin || '', avatar_url: member.avatar_url || '',
      searchable: !!member.searchable, conversations_topics: member.conversations_topics || [],
    });
  }, [member, loading, nav]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onSearchableChange = (checked) => {
    if (!checked && form.searchable) { setConfirmOptOut(true); return; }
    upd('searchable', checked);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Only images', description: 'Please upload a JPG or PNG.' }); return; }
    if (file.size > 3 * 1024 * 1024) { toast({ title: 'Too large', description: 'Max 3 MB.' }); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await memberApi.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      upd('avatar_url', data.url);
      toast({ title: 'Photo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await updateProfile(form); toast({ title: 'Profile saved' }); if (isFirstTime) nav('/member', { replace: true }); }
    catch { toast({ title: 'Could not save' }); }
    finally { setBusy(false); }
  };

  if (!member) return null;

  return (
    <MemberLayout>
      {confirmOptOut && (
        <OptOutModal onCancel={() => setConfirmOptOut(false)} onConfirm={() => { upd('searchable', false); setConfirmOptOut(false); }} />
      )}
      <div className="section-eyebrow text-[#0d2a20]/60 mb-2">Members · My Profile</div>
      <h1 className="hero-title text-[40px] md:text-[56px] leading-none mb-3">{isFirstTime ? 'Your first ' : 'Your '}<span className="font-serif-i text-[#e0684d]">profile</span></h1>
      <p className="text-[15px] text-[#0d2a20]/75 mb-6 max-w-[720px]">This is how your profile will appear to other members of the Vemoye Network. Your email and phone remain private and are never shown to other members.</p>

      <form onSubmit={save} className="rounded-lg p-6 md:p-8 space-y-6" style={{ background: '#fbf8ef' }}>
        {/* Photo */}
        <div className="flex items-center gap-5 flex-wrap">
          <Avatar src={form.avatar_url} name={form.display_name} size={80} />
          <div>
            <div className="text-[12px] text-[#0d2a20]/70 mb-2">Profile photo</div>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => uploadFile(e.target.files?.[0])} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 px-4 py-2 rounded-md btn-green text-[13px] disabled:opacity-60"><Upload size={14} /> {uploading ? 'Uploading…' : 'Upload photo'}</button>
            <div className="text-[11px] text-[#0d2a20]/55 mt-1">JPG or PNG, max 3 MB.</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="text-[12px] text-[#0d2a20]/70">Display name</label><input value={form.display_name} onChange={e => upd('display_name', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Professional headline / job title</label><input value={form.headline} onChange={e => upd('headline', e.target.value)} placeholder="e.g. Senior Product Manager" className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Organisation / company</label><input value={form.company} onChange={e => upd('company', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div>
            <label className="text-[12px] text-[#0d2a20]/70">Industry</label>
            <select value={form.industry} onChange={e => upd('industry', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }}>
              <option value="">Select industry…</option>
              {Array.from(new Set([...INDUSTRY_OPTIONS, form.industry].filter(Boolean))).map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div><label className="text-[12px] text-[#0d2a20]/70">Location</label><input value={form.location} onChange={e => upd('location', e.target.value)} placeholder="City, Country" className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
          <div><label className="text-[12px] text-[#0d2a20]/70">LinkedIn URL</label><input value={form.linkedin} onChange={e => upd('linkedin', e.target.value)} placeholder="https://linkedin.com/in/…" className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} /></div>
        </div>
        <div>
          <label className="text-[12px] text-[#0d2a20]/70">Skills / expertise</label>
          <div className="mt-1"><SkillPicker value={form.skills} onChange={v => upd('skills', v)} /></div>
          <div className="text-[11px] text-[#0d2a20]/55 mt-1">Pre-populated from your application. Add more to help others find you.</div>
        </div>
        <div>
          <label className="text-[12px] text-[#0d2a20]/70">Short professional bio</label>
          <textarea rows={4} value={form.bio} onChange={e => upd('bio', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} placeholder="A few lines about your work and what you care about." />
        </div>
        <div>
          <label className="text-[12px] text-[#0d2a20]/70">Conversations you're open to</label>
          <input value={(form.conversations_topics || []).join(', ')} onChange={e => upd('conversations_topics', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="e.g. hiring in Lagos, product for African markets" className="w-full mt-1 px-3 py-2 rounded-md bg-white border" style={{ borderColor: 'rgba(13,42,32,0.15)' }} />
          <div className="text-[11px] text-[#0d2a20]/55 mt-1">Comma-separated. Helps other members know when to reach out.</div>
        </div>

        <div className="rounded-md p-4" style={{ background: '#ece5d3' }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.searchable} onChange={e => onSearchableChange(e.target.checked)} className="w-4 h-4 mt-1 accent-[#e0684d]" />
            <span className="text-[14px] text-[#0d2a20]"><strong>Include my profile in the Vemoye Network Member Directory</strong><br/>
              <span className="text-[12px] text-[#0d2a20]/70">If you opt out, your profile is not discoverable AND you lose access to the directory.</span></span>
          </label>
        </div>

        <button disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md btn-coral text-[14px] disabled:opacity-60"><Save size={14} /> {busy ? 'Saving…' : (isFirstTime ? 'Save & continue' : 'Save profile')}</button>
      </form>
    </MemberLayout>
  );
};

export default Profile;
